import { date, datetime, format, getValueFromJson } from "../../utils";
import I18n from "../../I18n";
import { Buffer } from "buffer";

const processIdObject = (options, json) => {
    const cdmKeysToUse = getValueFromJson(options, "id");
    const idsToProxy = Object.entries(cdmKeysToUse || {}).reduce((acc, [cdmKey, cmdValue]) => {
        acc[`${cmdValue}`] = (json[cdmKey] || {}).value;
        return acc;
    }, {});

    return {
        id: Buffer.from(JSON.stringify(idsToProxy)).toString("base64"),
        name: (json[getValueFromJson(options, "name")] || {}).value,
        __typename: getValueFromJson(options, "__typename"),
    };
};

const processIdString = (options, json) => {
    const keyValue = (json[getValueFromJson(options, "id")] || {}).value;
    const cdmKeyToConnection = getValueFromJson(options, "cdmKey")
        ? `${getValueFromJson(options, "cdmKey")}.FKID`
        : `CDM_${getValueFromJson(options, "__typename")}s.FKID`;
    const idToProxy = {
        [`${cdmKeyToConnection}`]: keyValue,
    };
    return {
        id: Buffer.from(JSON.stringify(idToProxy)).toString("base64"),
        name: (json[getValueFromJson(options, "name")] || {}).value,
        __typename: getValueFromJson(options, "__typename"),
    };
};

const getFormattedValue = (value, defaultValue) => {
    return value !== undefined ? value : defaultValue;
};

const formatValueByType = (json, value, type, options, defaultValue, mask) => {
    let formatedValue = "";

    switch (type) {
        case "date":
            formatedValue = date(value, mask, false);
            break;
        case "currency":
            formatedValue = format(
                value,
                "normal",
                getValueFromJson(options, "currency"),
                getValueFromJson(options, "fractionDigits"),
                getValueFromJson(options, "locale"),
            );
            break;
        case "number":
            formatedValue = format(
                value,
                "normal",
                null,
                getValueFromJson(options, "fractionDigits"),
            );
            break;
        case "percentage":
            formatedValue = format(
                value,
                "normal",
                null,
                getValueFromJson(options, "fractionDigits"),
            );
            break;
        case "datetime":
            formatedValue = datetime(value, mask, false);
            break;
        case "object":
            //add retro compatibility
            const valueFromConfig = getValueFromJson(options, "id");
            const hasValue = (json[getValueFromJson(options, "id")] || {}).value;

            let objToReturn = null;

            switch (typeof valueFromConfig) {
                case "object":
                    objToReturn = processIdObject(options, json);
                    break;
                case "string":
                    objToReturn = processIdString(options, json);
                    break;
                default:
                    objToReturn = getFormattedValue(
                        (json[getValueFromJson(options, "name")] || {}).value,
                        defaultValue,
                    );
                    break;
            }

            objToReturn = hasValue
                ? objToReturn
                : (json[getValueFromJson(options, "name")] || {}).value || defaultValue;

            return {
                type: hasValue ? type : "string",
                value: objToReturn,
            };

        case "boolean":
            formatedValue = !!value === true ? I18n.translate(`Yes`) : I18n.translate(`No`);
            break;
        default:
            formatedValue = value || defaultValue;
            break;
    }

    return { type, value: formatedValue };
};

const parseRowData = (json, column) => {
    const { value, metadata, defaultValue } = json[column];
    const { label, options, type, mask, ...restMetadata } = metadata || {};

    return {
        labels: label || column,
        ...formatValueByType(json, value, type, options, defaultValue, mask),
        ...restMetadata,
    };
};

export const groupFieldsData = (data) => {
    return (data || [])
        .filter((elem) => elem.HideField === 0)
        .reduce((acc, elem) => {
            const unitCampista = parseInt(elem.CampistaOrder);

            if (acc[unitCampista]) {
                acc[unitCampista].push(elem);
            } else {
                acc[unitCampista] = [elem];
            }
            return acc;
        }, {});
};

const createGenericDetailView = (groupByColumn, groupedData, kpi) => {
    const positionToInsertKpi = (kpi && kpi.length && kpi[0].Position) || null;
    let mappedView = Object.keys(groupedData).map((key) => {
        let collapseFieldName = undefined;
        const groupedRowFields = (groupedData[key] || []).map((elem) => {
            const { value, metadata, defaultValue, column, CollapseFieldName, CampistaOrder } =
                elem;
            collapseFieldName =
                !collapseFieldName && CollapseFieldName ? CollapseFieldName : collapseFieldName;
            const { label, options, type, mask, ...restMetadata } = metadata || {};
            return {
                label: label || column,
                ...formatValueByType(groupByColumn, value, type, options, defaultValue, mask),
                ...restMetadata,
                CampistaOrder,
            };
        });

        if (collapseFieldName) {
            return {
                title: `${collapseFieldName}`,
                rows: [groupedRowFields],
            };
        } else {
            return groupedRowFields;
        }
    });
    if (positionToInsertKpi) {
        mappedView.splice(positionToInsertKpi - 1, 0, kpi);
    }

    return mappedView;
};

const mapCard = (json) => {
    return Object.keys(json).map((key) => {
        return json[key].hideField === 0 ? [parseRowData(json, key)] : null;
    });
};

const header = (json) => {
    return json.map((elem) => {
        const { SortableField, Metadata, CDMName, TotalizedField } = elem;
        const { label, ...rest } = JSON.parse(Metadata) || {};

        return {
            label: [label ? label : CDMName],
            sortable: SortableField || 0,
            columnName: CDMName,
            ...rest,
            totalizerName: TotalizedField ? `Total_${CDMName.replace(".", "_")}` : "none",
        };
    });
};

const mapDetails = (json, kpi) => {
    const groupedFieldsToShow = groupFieldsData(json.details);

    const groupByColumn = (json.details || []).reduce((acc, it) => {
        const { column, ...rest } = it;
        acc[column] = {
            column,
            ...rest,
        };
        return acc;
    }, {});

    const manipulateKpi = (kpi || []).map((e) => ({
        ...e,
        ...formatValueByType(e, e.value, e.type),
    }));

    return createGenericDetailView(groupByColumn, groupedFieldsToShow, manipulateKpi);
};

const mapReports = (reports) => {
    const resReports = (reports || []).map((report) => {
        const { title, tabs, ...rest } = report;
        return {
            ...rest,
            title: I18n.translate(title || ""),
            tabs: (tabs || []).map((title) => I18n.translate(title || "")),
        };
    });
    return resReports && resReports.length ? resReports : null;
};

const mapper = (json, card, details, locale, entity, modules, kpi) => {
    try {
        const tempObj = json[`${entity}.ID`];
        return {
            id: tempObj && tempObj.hasDetail ? tempObj && tempObj.value : null,
            ...json,
            shortName: details
                ? (
                      (json.details || []).find(
                          (elem) => elem.column === `${json.cdmEntity}.ShortName`,
                      ) || {}
                  ).value
                : null,
            card: card ? mapCard(json) : [],
            details: details ? mapDetails(json, kpi) : [],
            reports: mapReports(json.reports || []),
        };
    } catch (error) {
        console.log(error);
        return {};
    }
};

const genericviewMapper = {
    map: mapper,
    header,
};

export default genericviewMapper;
